import React, { useState, useEffect } from 'react';
import { useStore } from '@/core/store';
import { useShallow } from 'zustand/react/shallow';
import { cn } from '@/lib/utils';

interface SkeletonProps {
    className?: string;
}

const Shimmer = ({ className }: SkeletonProps) => (
    <div className={cn("bg-white/5 animate-pulse rounded-xl", className)} />
);

// For Creative Studio, Video Studio, Merch
const StudioSkeleton = () => (
    <div className="flex-1 flex gap-4 h-full p-4">
        {/* Left Toolbar */}
        <div className="w-16 h-full flex flex-col gap-2">
            <Shimmer className="w-12 h-12 rounded-lg" />
            <Shimmer className="w-12 h-12 rounded-lg" />
            <Shimmer className="w-12 h-12 rounded-lg" />
            <Shimmer className="w-12 h-12 rounded-lg mt-auto" />
        </div>
        {/* Main Canvas */}
        <div className="flex-1 h-full">
            <Shimmer className="w-full h-full" />
        </div>
        {/* Right Properties Panel */}
        <div className="w-72 h-full flex flex-col gap-4">
            <Shimmer className="w-full h-32" />
            <Shimmer className="w-full flex-1" />
        </div>
    </div>
);

// For Dashboard, Finance, Marketing
const DashboardSkeleton = () => (
    <div className="h-full flex flex-col p-6 gap-6">
        {/* Header */}
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
                <Shimmer className="h-10 w-10" />
                <Shimmer className="h-8 w-48" />
            </div>
            <div className="flex gap-2">
                <Shimmer className="h-10 w-24" />
                <Shimmer className="h-10 w-24" />
            </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4">
            <Shimmer className="h-32" />
            <Shimmer className="h-32" />
            <Shimmer className="h-32" />
            <Shimmer className="h-32" />
        </div>

        {/* Main Content Area */}
        <div className="grid grid-cols-3 gap-6 flex-1">
            <div className="col-span-2 space-y-6 flex flex-col">
                <Shimmer className="h-64" />
                <Shimmer className="flex-1" />
            </div>
            <div className="space-y-6 flex flex-col">
                <Shimmer className="h-80" />
                <Shimmer className="flex-1" />
            </div>
        </div>
    </div>
);

// For Agent, Support, Social
const ChatSkeleton = () => (
    <div className="flex h-full p-4 gap-4">
        {/* Thread List */}
        <div className="w-80 h-full flex flex-col gap-3">
            <Shimmer className="h-12 w-full" />
            <div className="flex-1 flex flex-col gap-2 mt-4">
                <Shimmer className="h-20 w-full" />
                <Shimmer className="h-20 w-full" />
                <Shimmer className="h-20 w-full" />
                <Shimmer className="h-20 w-full" />
            </div>
        </div>
        {/* Main Chat Area */}
        <div className="flex-1 h-full flex flex-col gap-4">
            <Shimmer className="h-16 w-full" />
            <div className="flex-1 flex flex-col justify-end gap-4 pb-4">
                <Shimmer className="h-16 w-2/3 ml-auto" />
                <Shimmer className="h-24 w-1/2" />
                <Shimmer className="h-16 w-3/4 ml-auto" />
            </div>
            <Shimmer className="h-16 w-full" />
        </div>
    </div>
);

// Fallback Generic
const GenericSkeleton = () => (
    <div className="h-full flex flex-col p-6 gap-4">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
                <Shimmer className="h-8 w-8" />
                <Shimmer className="h-5 w-40" />
            </div>
            <div className="flex gap-2">
                <Shimmer className="h-8 w-20" />
                <Shimmer className="h-8 w-20" />
            </div>
        </div>

        {/* Content skeleton */}
        <div className="flex-1 grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-4 flex flex-col">
                <Shimmer className="h-48" />
                <div className="grid grid-cols-2 gap-4 flex-1">
                    <Shimmer className="h-full" />
                    <Shimmer className="h-full" />
                </div>
            </div>
            <div className="space-y-4 flex flex-col">
                <Shimmer className="h-32" />
                <Shimmer className="h-32" />
                <Shimmer className="flex-1" />
            </div>
        </div>
    </div>
);

export function LoadingFallback() {
    const { currentModule } = useStore(useShallow(state => ({
        currentModule: state.currentModule
    })));
    const [show, setShow] = useState(false);
    const [isTimedOut, setIsTimedOut] = useState(false);

    useEffect(() => {
        // Delay showing the skeleton to prevent flash for fast module loads
        const timer = setTimeout(() => setShow(true), 150);
        
        // 10s failsafe timeout to prevent infinite loading spinners
        const failsafe = setTimeout(() => {
            setIsTimedOut(true);
            console.warn(`[LoadingFallbacks] Module loading timed out for: ${currentModule}`);
        }, 10000);

        return () => {
            clearTimeout(timer);
            clearTimeout(failsafe);
        };
    }, [currentModule]); // Reset delay when module changes

    if (isTimedOut) {
        return (
            <div className="absolute inset-0 bg-background/95 backdrop-blur-sm z-[50] flex flex-col items-center justify-center p-6 text-center animate-in fade-in">
                <div className="text-4xl mb-4">⏳</div>
                <h3 className="text-lg font-bold text-white mb-2">Loading is taking longer than expected</h3>
                <p className="text-gray-400 text-sm max-w-sm mb-6">
                    A background service or module is taking a long time to load. You can continue waiting or try reloading.
                </p>
                <div className="flex gap-4">
                    <button onClick={() => window.location.reload()} className="px-6 py-2.5 bg-dept-creative text-white rounded-lg text-sm font-semibold hover:bg-dept-creative-glow transition-colors">
                        Reload App
                    </button>
                    <button onClick={() => { setIsTimedOut(false); useStore.getState().setModule('dashboard'); }} className="px-6 py-2.5 bg-white/10 text-white rounded-lg text-sm hover:bg-white/20 transition-colors">
                        Go to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    if (!show) {
        return null;
    }

    let SkeletonComponent = GenericSkeleton;

    if (['creative', 'video', 'merch', 'capture', 'workflow', 'files'].includes(currentModule)) {
        SkeletonComponent = StudioSkeleton;
    } else if (['dashboard', 'finance', 'marketing', 'distribution', 'publishing', 'legal', 'licensing', 'brand', 'campaign', 'road', 'knowledge', 'observability', 'history', 'debug', 'onboarding', 'select-org', 'investor'].includes(currentModule)) {
        SkeletonComponent = DashboardSkeleton;
    } else if (['agent', 'social', 'publicist'].includes(currentModule)) {
        SkeletonComponent = ChatSkeleton;
    }

    return (
        <div className="absolute inset-0 bg-background/95 backdrop-blur-sm animate-in fade-in duration-200 z-[40]">
            <SkeletonComponent />
        </div>
    );
}
